/**
 * The toolbar `⋯` menu (spec M1-B4 §8.11, `[c:toolbar-more]`, 250 × 4 rows).
 *
 * Four entries, in this order: **Pin columns · Item height · Conditional coloring · Default
 * item values**. The last two are reserved settings keys only (Q173) and render disabled.
 *
 * **P-083 (round 10e): `Item height` is live.** It had been a disabled placeholder since round
 * B10 while every piece of state behind it was already built and correct — `ItemHeight`,
 * `ITEM_HEIGHT_PX` and `effectiveRowHeight()` in `settings.ts`, and `Grid.tsx` re-measuring the
 * virtualiser whenever the derived row height changes (6i / q455) — so every board was stuck on
 * monday's 36 px rows with the control to change them greyed out. It writes
 * `setEffective({ itemHeight })`, which is personal view state and rides the same
 * `views.setUserState` path as the rest, so it persists and saves to the view for free.
 *
 * Only `small` is measured on monday `[B4§8.1]`; 44 px and 56 px are this product's reading of
 * `medium` and `large` and must be photographed if the sandbox is re-opened — **q508**.
 *
 * `Pin columns` keeps its column list **inline** rather than in a flyout: pinned columns must be
 * a prefix of the order (Q167), so the list is the visible order with a ✓ per pinned column.
 */
import { useTable, useTableActions } from '../context';
import { ITEM_HEIGHT_PX, type ItemHeight } from '../settings';
import { NAME_COLUMN_ID } from '../store';
import { Menu, type MenuEntry } from './Menu';

/** monday's three pitches, in monday's order `[c:toolbar-more]`. */
const ITEM_HEIGHTS: readonly { id: ItemHeight; label: string }[] = [
  { id: 'small', label: 'Small' },
  { id: 'medium', label: 'Medium' },
  { id: 'large', label: 'Large' },
];

export function MoreMenu({ x, y }: { x: number; y: number }) {
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const actions = useTableActions();

  const entries: MenuEntry[] = [
    {
      id: 'pin',
      label: 'Pin columns',
      submenu: [],
      onSelect: () => actions.openPane('hide'),
    },
    {
      id: 'item-height',
      label: 'Item height',
      'data-testid': 'more-menu-item-height',
      submenu: ITEM_HEIGHTS.map((height) => ({
        id: `item-height-${height.id}`,
        label: height.label,
        'data-testid': `item-height-${height.id}`,
        ...(effective.itemHeight === height.id ? { hint: '✓' } : {}),
        title: `${ITEM_HEIGHT_PX[height.id]} px rows`,
        onSelect: () => actions.setEffective({ itemHeight: height.id }),
      })),
    },
    { id: 'conditional', label: 'Conditional coloring', disabled: true },
    { id: 'defaults', label: 'Default item values', disabled: true },
    { id: 'd1', label: '', divider: true },
    ...(payload?.columns ?? [])
      .filter((column) => column.id !== NAME_COLUMN_ID)
      .slice(0, 8)
      .map((column): MenuEntry => {
        const pinned = effective.pinnedColumnIds.includes(column.id);
        return {
          id: `pin-${column.id}`,
          label: column.title,
          'data-testid': `pin-${column.id}`,
          ...(pinned ? { hint: '✓' } : {}),
          onSelect: () => actions.pinColumn(column.id, !pinned),
        };
      }),
  ];

  return (
    <Menu
      entries={entries}
      x={x}
      y={y}
      label="Board options"
      onClose={() => actions.openMenu(null)}
    />
  );
}
